/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, type ReactElement } from 'react';
import { Await, useMatches } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Region } from '@/components/region';
import { cn } from '@/lib/utils';
import { type DefaultSettingsTheme } from '@/lib/page-designer/default-settings';
import type { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
import FooterLink from './footer-link';
import FooterSocialLink from './footer-social-link';
import FooterTagline from './footer-tagline';
// Import as a Vite asset (like the logos) so the URL resolves in dev AND the MRT build.
// A raw `/images/...` path works in the dev server but 404s on Managed Runtime.
import footerBg from '/images/footer-bg.webp';

export type FooterPage = ReturnType<typeof fetchPageWithComponentData>;
type ResolvedFooterPage = Awaited<FooterPage>;

/**
 * A route's `footerTheme`: the theme of the last Page Designer component on the current page (see
 * `resolveLastComponentTheme`), used to continue the footer background from the final on-page section.
 * Routes that await their page expose a resolved value; routes that stream it (home) expose a promise.
 */
type FooterThemeInput = DefaultSettingsTheme | Promise<DefaultSettingsTheme> | undefined;

function isThemeColor(value: unknown): value is DefaultSettingsTheme {
    return value === 'dark' || value === 'light';
}

/**
 * Read the footer background theme from the **current page**, not the footer page type: each route
 * exposes a `footerTheme` in its loader data, which we read off the leaf route match. Absent (non-PD
 * routes like cart/checkout/account) → the footer defaults to light.
 */
function useLeafFooterTheme(): FooterThemeInput {
    const matches = useMatches();
    const leaf = matches[matches.length - 1];
    return (leaf?.data as { footerTheme?: FooterThemeInput } | undefined)?.footerTheme;
}

// Footer nav links — Body XL (26px desktop / 20px mobile, semibold) per the Figma footer.
const navColumnClass = 'flex flex-col items-start gap-4 type-body-xl';
const socialRowClass = 'flex items-center gap-6';
const legalRowClass = 'flex flex-wrap items-center gap-x-5 gap-y-1';

/**
 * Hardcoded backup content — rendered when the `footer` Page Designer page is missing,
 * empty, or fails to load. Reuses the same PD components so the fallback looks identical
 * to authored content. Merchandisers override all of this by publishing the `footer` page.
 */
function DefaultNav(): ReactElement {
    return (
        <nav className={navColumnClass} aria-label="Footer">
            <FooterLink label="About" url="/about-us" />
            <FooterLink label="FAQ" url="#" />
            <FooterLink label="Contact" url="#" />
            <FooterLink label="Shipping & Handling" url="#" />
            <FooterLink label="Soil science" url="#" />
            <FooterLink label="Send us feedback" url="#" />
        </nav>
    );
}

function DefaultSocial(): ReactElement {
    return (
        <div className={socialRowClass}>
            <FooterSocialLink platform="instagram" url="#" />
            <FooterSocialLink platform="facebook" url="#" />
            <FooterSocialLink platform="youtube" url="#" />
        </div>
    );
}

function DefaultTagline(): ReactElement {
    return <FooterTagline primaryText="Build Living Soil" />;
}

function DefaultLegal(): ReactElement {
    return (
        <div className={legalRowClass}>
            <FooterLink label="Terms of Use" url="/terms" />
            <FooterLink label="Privacy Policy" url="/privacy" />
        </div>
    );
}

/**
 * The footer chrome, with the background color driven by `theme`. The footer's background *image* is
 * dark, so the **text is always light** in both themes — we intentionally do NOT apply the
 * `bm-theme-*` class (which would also flip the text color); only the background color changes,
 * matching the theme background tokens (dark = `--bm-black`, light = `--bm-organic-white`).
 *
 * The image has a torn top edge with transparency above it, so on the light theme this color is
 * visible as a strip between the page and the graphic — it therefore has to be the same Organic
 * White as the site ground (`--background`, core.css), or that strip reads as a brighter band.
 */
function FooterFrame({
    page,
    theme,
}: {
    page: FooterPage | ResolvedFooterPage;
    theme: DefaultSettingsTheme;
}): ReactElement {
    const { t } = useTranslation('footer');
    const themeBg = theme === 'light' ? 'bg-[var(--bm-organic-white)]' : 'bg-[var(--bm-black)]';

    return (
        <footer
            className={cn('mt-auto bg-cover bg-top text-[var(--bm-organic-white)]', themeBg)}
            style={{ backgroundImage: `url(${footerBg})` }}>
            <div className="footer-container py-10 lg:py-12">
                {/* Extra 40px above the columns for breathing room. Mobile order (single column):
                    nav+social → tagline → legal. Desktop is a 2-column grid — nav+social (top) and
                    legal (bottom) fill the left column, the tagline the right column — so the DOM order
                    that mobile needs is reflowed with explicit grid placement, not reordered markup. */}
                <div className="flex flex-col gap-12 pt-10 lg:grid lg:grid-cols-[auto_minmax(0,55%)] lg:items-start lg:justify-between lg:gap-x-12 lg:gap-y-16">
                    {/* Navigation + social — desktop top-left */}
                    <div className="flex flex-col gap-8 lg:col-start-1 lg:row-start-1 lg:gap-10">
                        <Region
                            page={page}
                            regionId="nav"
                            className={navColumnClass}
                            fallbackOnEmpty
                            fallbackElement={<DefaultNav />}
                            errorElement={<DefaultNav />}
                        />
                        <div className="flex flex-col gap-6">
                            {/* "Follow Us" headline — H3 (48px desktop / 32px mobile) per the Figma footer. */}
                            <span className="type-h3">{t('followUs', 'Follow Us')}</span>
                            <Region
                                page={page}
                                regionId="social"
                                className={socialRowClass}
                                fallbackOnEmpty
                                fallbackElement={<DefaultSocial />}
                                errorElement={<DefaultSocial />}
                            />
                        </div>
                    </div>

                    {/* Brand tagline — mobile: between Follow Us and legal; desktop: right column,
                        spanning both rows. items-center centers the staggered cluster (the per-word
                        indents are preserved inside the block). */}
                    <div className="flex flex-col items-center lg:col-start-2 lg:row-start-1 lg:row-span-2">
                        <Region
                            page={page}
                            regionId="tagline"
                            fallbackOnEmpty
                            fallbackElement={<DefaultTagline />}
                            errorElement={<DefaultTagline />}
                        />
                    </div>

                    {/* Legal links + copyright — one inline row, muted; desktop bottom-left */}
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-[var(--bm-organic-white)]/60 lg:col-start-1 lg:row-start-2">
                        <Region
                            page={page}
                            regionId="legal"
                            className={legalRowClass}
                            fallbackOnEmpty
                            fallbackElement={<DefaultLegal />}
                            errorElement={<DefaultLegal />}
                        />
                        <span>
                            © {new Date().getFullYear()} {t('copyright')}
                        </span>
                    </div>
                </div>
            </div>
        </footer>
    );
}

export default function MainFooter({ footerPage }: { footerPage?: FooterPage }): ReactElement {
    const page = footerPage ?? null;
    const footerTheme = useLeafFooterTheme();

    // The footer content (regions) comes from the `footer` page; the background theme comes from the
    // current route's `footerTheme`. On home the theme is a promise (the page streams) — resolve it in
    // an Await, below the fold, with a light fallback. A resolved value (routes that await their page)
    // or an absent value (non-PD routes) renders directly, defaulting to light. Only the background
    // color changes once the theme resolves, so there's no layout shift.
    if (footerTheme && typeof (footerTheme as { then?: unknown }).then === 'function') {
        return (
            <Suspense fallback={<FooterFrame page={page} theme="light" />}>
                <Await
                    resolve={footerTheme as Promise<DefaultSettingsTheme>}
                    errorElement={<FooterFrame page={page} theme="light" />}>
                    {(resolved) => <FooterFrame page={page} theme={isThemeColor(resolved) ? resolved : 'light'} />}
                </Await>
            </Suspense>
        );
    }

    return <FooterFrame page={page} theme={isThemeColor(footerTheme) ? footerTheme : 'light'} />;
}
